import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { debounce } from '../../../utils/debounce';
import { Input } from '../Input';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';

import { HighlightedLabel } from './HighlightedLabel';
import type { SearchableSelectProps } from './types';

const SEARCH_WAIT_MS = 250;
// How near the end of the list, in pixels or rows, the next page is asked for:
// early enough that it lands before the CFO reaches the bottom.
const LOAD_MORE_EDGE_PX = 48;
const LOAD_MORE_EDGE_ROWS = 3;
const SKELETON_ROWS = 4;

const TRIGGER_CLASS =
  'flex h-9 cursor-pointer items-center justify-between gap-2 rounded-md border border-transparent bg-control px-2.5 py-1 text-sm text-control-foreground transition-colors hover:bg-control-hover focus-visible:border-focus focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-control-disabled disabled:text-control-disabled-foreground';

// A plain select is fine for five options and useless for fifty: the CFO knows
// the client's name and should be able to type it rather than hunt a list that
// scrolls past the fold. Focus stays in the search box; the arrow keys move a
// highlight through the list and Enter picks it.
export function SearchableSelect({
  value,
  options,
  placeholder,
  ariaLabel,
  disabled,
  emptyLabel,
  variant = 'field',
  onChange,
  onSearch,
  loading = false,
  hasMore = false,
  onLoadMore,
  selectedLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const optionId = (index: number) => `${listId}-option-${index}`;

  // Pages hand a fresh onSearch on each render; the debounce reads the latest
  // one so a re-render mid-typing does not cancel what was typed.
  const onSearchRef = useRef(onSearch);
  useLayoutEffect(() => {
    onSearchRef.current = onSearch;
  });
  const remote = onSearch !== undefined;
  const search = useMemo(
    () => (remote ? debounce((q: string) => onSearchRef.current?.(q), SEARCH_WAIT_MS) : null),
    [remote],
  );
  useEffect(() => () => search?.cancel(), [search]);

  const searching = query.trim() !== '';
  const { pinned, results } = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const entries = options.filter((option) => !option.pinned);
    return {
      pinned: needle === '' ? options.filter((option) => option.pinned) : [],
      results:
        onSearch || needle === ''
          ? entries
          : entries.filter((option) => option.label.toLowerCase().includes(needle)),
    };
  }, [onSearch, options, query]);
  const rows = [...pinned, ...results];

  const current = Math.max(0, Math.min(active, rows.length - 1));
  const chosen =
    options.find((option) => option.value === value) ??
    (value && selectedLabel ? { value, label: selectedLabel, pinned: false } : null);
  const showsPlaceholder = chosen === null || chosen.pinned === true;

  useEffect(() => {
    if (!open) return;
    document.getElementById(`${listId}-option-${current}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [open, current, listId]);

  const loadMore = () => {
    if (hasMore && !loading) onLoadMore?.();
  };

  const pick = (next: string) => {
    onChange(next);
    setQuery('');
    setOpen(false);
  };

  const type = (next: string) => {
    setQuery(next);
    setActive(0);
    search?.(next.trim());
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const next = Math.min(current + 1, rows.length - 1);
      setActive(next);
      if (next >= rows.length - LOAD_MORE_EDGE_ROWS) loadMore();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = rows[current];
      if (option && !option.disabled) pick(option.value);
    }
  };

  const onListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const list = event.currentTarget;
    if (list.scrollHeight - list.scrollTop - list.clientHeight < LOAD_MORE_EDGE_PX) loadMore();
  };

  const row = (option: (typeof rows)[number], index: number) => {
    const selected = option.value === value;
    return (
      <div
        key={option.value}
        id={optionId(index)}
        role="option"
        // The highlight splits the label into pieces; the name stays whole.
        aria-label={option.label}
        aria-selected={selected}
        aria-disabled={option.disabled || undefined}
        onMouseMove={() => setActive(index)}
        onClick={() => !option.disabled && pick(option.value)}
        className={cn(
          'flex h-8 cursor-pointer select-none items-center justify-between gap-2 rounded-md px-2 text-sm text-text-primary',
          index === current && 'bg-listbox-active',
          option.pinned && 'text-text-secondary',
          selected && 'font-medium',
          option.disabled && 'cursor-default text-text-tertiary',
        )}
      >
        <HighlightedLabel label={option.label} query={query} />
        {selected && <Check className="size-4 shrink-0 text-primary" aria-hidden="true" />}
      </div>
    );
  };

  return (
    // Modal so the list is its own scroll layer: inside a dialog, whose scroll
    // lock covers everything outside it, the portalled list would otherwise
    // swallow the mouse wheel.
    <Popover
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setQuery('');
          search?.cancel();
        } else {
          const chosenAt = rows.findIndex((option) => option.value === value);
          setActive(chosenAt >= 0 ? chosenAt : 0);
          onSearch?.('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          className={cn(TRIGGER_CLASS, variant === 'inline' ? 'w-fit' : 'w-full')}
        >
          {variant === 'inline' ? (
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="font-medium text-muted-foreground">{ariaLabel}:</span>
              <span className="truncate">{chosen?.label ?? placeholder}</span>
            </span>
          ) : (
            <span className={cn('truncate', showsPlaceholder && 'text-control-placeholder')}>
              {chosen?.label ?? placeholder}
            </span>
          )}
          {variant === 'inline' ? (
            <ChevronDown className="size-4 shrink-0 opacity-60" aria-hidden="true" />
          ) : (
            <ChevronsUpDown className="size-4 shrink-0 text-text-tertiary" aria-hidden="true" />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        sideOffset={6}
        collisionPadding={12}
        className="flex w-[var(--radix-popover-trigger-width)] min-w-72 flex-col overflow-hidden rounded-lg p-0 shadow-lg"
      >
        <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border-faint px-3">
          {loading ? (
            <Loader2
              className="size-4 shrink-0 animate-spin text-text-tertiary"
              aria-hidden="true"
            />
          ) : (
            <Search className="size-4 shrink-0 text-text-tertiary" aria-hidden="true" />
          )}
          <Input
            autoFocus
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={rows.length > 0 ? optionId(current) : undefined}
            value={query}
            onChange={(event) => type(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder={`Search ${ariaLabel.toLowerCase()}…`}
            aria-label={`Search ${ariaLabel}`}
            className="h-full flex-1 border-0 bg-transparent px-0 text-sm focus-visible:border-0"
          />
          {searching && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => type('')}
              className="flex size-5 shrink-0 cursor-pointer items-center justify-center rounded-sm text-text-tertiary transition-colors hover:text-text-primary"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        <div
          id={listId}
          role="listbox"
          aria-label={ariaLabel}
          aria-busy={loading}
          onScroll={onListScroll}
          className={cn(
            'max-h-[min(18rem,calc(var(--radix-popover-content-available-height)-6rem))] overflow-y-auto overscroll-contain p-1 transition-opacity [&::-webkit-scrollbar-button]:hidden [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-listbox-scrollbar [&::-webkit-scrollbar]:w-1.5',
            // A new search keeps the old results on screen, faded, until its
            // own arrive, so the list never flashes empty between keystrokes.
            loading && results.length > 0 && 'opacity-60',
          )}
        >
          {pinned.map((option, index) => row(option, index))}
          {pinned.length > 0 && results.length > 0 && (
            <div role="separator" className="-mx-1 my-1 h-px bg-border-faint" />
          )}
          {results.map((option, index) => row(option, pinned.length + index))}

          {loading && results.length === 0 && (
            <div className="flex flex-col gap-1 py-0.5" aria-hidden="true">
              {Array.from({ length: SKELETON_ROWS }, (_, i) => (
                <div key={i} className="flex h-8 items-center px-2">
                  <div
                    className="h-2.5 animate-pulse rounded-full bg-listbox-active"
                    style={{ width: `${70 - i * 12}%` }}
                  />
                </div>
              ))}
            </div>
          )}
          {loading && (
            <p
              role="status"
              className={cn(
                results.length === 0
                  ? 'sr-only'
                  : 'px-2 py-1.5 text-center text-xs text-text-tertiary',
              )}
            >
              Searching…
            </p>
          )}
          {!loading && results.length === 0 && (
            <div className="px-3 py-6 text-center">
              <p className="text-sm text-text-secondary">{emptyLabel ?? 'Nothing matches'}</p>
              {searching && (
                <p className="mt-1 text-xs text-text-tertiary">
                  Nothing for “{query.trim()}”. Try another spelling.
                </p>
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
